import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const ATF_LOGO = "/logo.png";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "How We Work", to: "/how-we-work" },
  { label: "Services", to: "/services" },
  { label: "About", to: "/about" },
  { label: "Careers", to: "/careers" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <>
      <nav
        data-testid="navbar"
        className={`sticky top-0 left-0 right-0 z-50 transition-all duration-500 bg-[#F9F8F5] border-b border-[#835F21]/10 py-4 shadow-sm`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo" className="flex items-center gap-4 group">
            <div className="w-14 h-14 transition-all duration-300 flex-shrink-0">
              <img
                src={ATF_LOGO}
                alt="ATF Logo"
                className="w-full h-full object-contain filter drop-shadow-md"
                loading="eager"
                onError={(e) => {
                  e.target.src = "https://via.placeholder.com/150?text=ATF";
                }}
              />
            </div>
            <div className="leading-tight">
              <span className={`font-sans font-extrabold text-lg tracking-[0.15em] block transition-colors duration-300 ${
                scrolled ? "text-[#141414]" : "text-[#141414]"
              }`}>
                ASTRAUM
              </span>
              <span className="font-sans font-bold text-[10px] tracking-[0.3em] text-[#835F21] uppercase block">
                Task Force
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`nav-link font-sans text-[11px] font-bold tracking-[0.2em] uppercase transition-colors duration-300 ${
                  isActive(link.to) 
                    ? "text-[#835F21] active" 
                    : "text-[#141414] hover:text-[#835F21]"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className={`btn-primary h-10 px-6 text-[10px] transition-all duration-300`}
            >
              Contact Us
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            data-testid="mobile-menu-toggle"
            className={`md:hidden p-2 transition-colors ${scrolled ? "text-[#141414]" : "text-[#141414]"}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Drawer Overlay */}
      {menuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={() => setMenuOpen(false)}
          data-testid="mobile-menu-overlay"
        />
      )}

      {/* Mobile Drawer */}
      <div
        data-testid="mobile-menu-drawer"
        className={`fixed top-0 right-0 h-full w-72 bg-[#F9F8F5] z-50 flex flex-col pt-20 px-8 pb-8 shadow-2xl transition-transform duration-300 ${
          menuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <button
          className="absolute top-5 right-5 p-2 text-[#141414]"
          onClick={() => setMenuOpen(false)}
          aria-label="Close menu"
        >
          <X size={22} />
        </button>

        <div className="flex items-center gap-4 mb-10">
          <div className="w-16 h-16">
            <img src={ATF_LOGO} alt="ATF" className="w-full h-full object-contain" />
          </div>
          <div>
            <span className="font-sans font-extrabold text-lg tracking-widest text-[#141414] block">ASTRAUM</span>
            <span className="font-sans text-[10px] font-bold tracking-[0.2em] text-[#835F21] uppercase block">Task Force</span>
          </div>
        </div>

        <nav className="flex flex-col gap-2">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-sans text-base font-medium py-3 border-b border-[#835F21]/10 transition-colors ${
                isActive(link.to) ? "text-[#835F21]" : "text-[#141414] hover:text-[#835F21]"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="mt-8">
          <Link
            to="/contact"
            data-testid="mobile-cta-button"
            className="btn-primary w-full justify-center"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </>
  );
}
